import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import type {
	CreateTicketAttachmentBody,
	CreateTicketBody,
	CreateTicketCommentBody,
	MyTicketsResponse,
	SetTicketStatusBody,
	TicketAttachmentDTO,
	TicketAttachmentUploadResponse,
	TicketCommentDTO,
	TicketCommentListResponse,
	TicketListResponse,
	TicketResponse,
	TicketStatus,
	UpdateTicketBody,
} from "@zplanem/responses"
import { TICKET_STATUSES } from "@zplanem/responses"
import { ApiError, apiFetch, uploadToCloudinary } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import {
	type MyTicketListFilters,
	myTicketsApiPath,
	type PropertyTicketListFilters,
	ticketAttachmentConfirmApiPath,
	ticketAttachmentCreateApiPath,
	ticketAttachmentDeleteApiPath,
	ticketBoardCacheRoot,
	ticketCommentApiPath,
	ticketCommentsApiPath,
	ticketDetailApiPath,
	ticketListApiPath,
	ticketStatusApiPath,
} from "./ticket-api-paths"
import { optimisticTicketStatusMove } from "./ticket-cache-model"

type TicketCommentResponse = { comment: TicketCommentDTO }
type TicketAttachmentResponse = { attachment: TicketAttachmentDTO }

type TicketAttachmentTarget = { commentId?: string }

async function invalidateTicketQueries(
	queryClient: ReturnType<typeof useQueryClient>,
	propertyId: string,
	ticketId: string,
) {
	await Promise.all([
		queryClient.invalidateQueries({
			queryKey: ticketBoardCacheRoot(propertyId),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.ticket(propertyId, ticketId),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.ticketComments(propertyId, ticketId),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.myTicketsRoot(),
		}),
	])
}

export function usePropertyTickets(
	propertyId: string | undefined,
	filters: Omit<PropertyTicketListFilters, "cursor" | "limit">,
) {
	return useInfiniteQuery({
		queryKey: propertyId
			? queryKeys.propertyTickets(propertyId, filters)
			: ["properties", "__nil__", "tickets", filters],
		queryFn: ({ pageParam }) =>
			apiFetch<TicketListResponse>(
				ticketListApiPath(propertyId as string, {
					...filters,
					cursor: pageParam,
					limit: 50,
				}),
			),
		getNextPageParam: (last) => last.nextCursor ?? undefined,
		initialPageParam: null as string | null,
		enabled: !!propertyId,
	})
}

export function useMyTickets(
	filters: Omit<MyTicketListFilters, "cursor" | "limit"> = {},
) {
	return useInfiniteQuery({
		queryKey: queryKeys.myTickets(filters),
		queryFn: ({ pageParam }) =>
			apiFetch<MyTicketsResponse>(
				myTicketsApiPath({
					...filters,
					cursor: pageParam,
					limit: 50,
				}),
			),
		getNextPageParam: (last) => last.nextCursor ?? undefined,
		initialPageParam: null as string | null,
	})
}

export function useTicket(
	propertyId: string | undefined,
	ticketId: string | undefined,
) {
	return useQuery({
		queryKey:
			propertyId && ticketId
				? queryKeys.ticket(propertyId, ticketId)
				: ["properties", "__nil__", "tickets", "__nil__"],
		queryFn: () =>
			apiFetch<TicketResponse>(
				ticketDetailApiPath(propertyId as string, ticketId as string),
			),
		enabled: !!propertyId && !!ticketId,
		retry: (failureCount, error) => {
			if (error instanceof ApiError && error.status === 404) return false
			return failureCount < 3
		},
	})
}

export function useCreateTicket(propertyId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateTicketBody) =>
			apiFetch<TicketResponse>(ticketListApiPath(propertyId, {}), {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ticketBoardCacheRoot(propertyId),
			})
			await queryClient.invalidateQueries({
				queryKey: queryKeys.myTicketsRoot(),
			})
		},
	})
}

export function useUpdateTicket(propertyId: string, ticketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: UpdateTicketBody) =>
			apiFetch<TicketResponse>(ticketDetailApiPath(propertyId, ticketId), {
				method: "PATCH",
				body: JSON.stringify(body),
			}),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ticketBoardCacheRoot(propertyId),
			})
			await queryClient.invalidateQueries({
				queryKey: queryKeys.ticket(propertyId, ticketId),
			})
			await queryClient.invalidateQueries({
				queryKey: queryKeys.myTicketsRoot(),
			})
		},
	})
}

export function useSetTicketStatus(propertyId: string, ticketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: SetTicketStatusBody) =>
			apiFetch<TicketResponse>(ticketStatusApiPath(propertyId, ticketId), {
				method: "POST",
				body: JSON.stringify(body),
			}),
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}

export function useSetAnyTicketStatus(
	propertyId: string,
	filters: Omit<PropertyTicketListFilters, "status" | "cursor" | "limit"> = {},
) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			ticketId,
			status,
		}: {
			ticketId: string
			status: TicketStatus
		}) =>
			apiFetch<TicketResponse>(ticketStatusApiPath(propertyId, ticketId), {
				method: "POST",
				body: JSON.stringify({ status } satisfies SetTicketStatusBody),
			}),
		onMutate: async (vars) => {
			const mutationFilters = { ...filters }
			await queryClient.cancelQueries({
				queryKey: ticketBoardCacheRoot(propertyId),
			})
			const snapshots = new Map<
				TicketStatus,
				InfiniteData<TicketListResponse>
			>()
			const caches = TICKET_STATUSES.map((status) => {
				const data = queryClient.getQueryData<InfiniteData<TicketListResponse>>(
					queryKeys.propertyTickets(propertyId, {
						...mutationFilters,
						status,
					}),
				)
				if (data) snapshots.set(status, data)
				return { status, data }
			})
			const moved = optimisticTicketStatusMove(
				caches,
				vars.ticketId,
				vars.status,
			)
			if (moved) {
				for (const [status, data] of moved) {
					queryClient.setQueryData(
						queryKeys.propertyTickets(propertyId, {
							...mutationFilters,
							status,
						}),
						data,
					)
				}
			}
			return { filters: mutationFilters, snapshots }
		},
		onError: (_error, _vars, context) => {
			for (const [status, data] of context?.snapshots ?? []) {
				queryClient.setQueryData(
					queryKeys.propertyTickets(propertyId, {
						...context?.filters,
						status,
					}),
					data,
				)
			}
		},
		onSuccess: async (_data, vars) => {
			await invalidateTicketQueries(queryClient, propertyId, vars.ticketId)
		},
	})
}

export function useTicketComments(
	propertyId: string | undefined,
	ticketId: string | undefined,
) {
	return useQuery({
		queryKey:
			propertyId && ticketId
				? queryKeys.ticketComments(propertyId, ticketId)
				: ["properties", "__nil__", "tickets", "__nil__", "comments"],
		queryFn: () =>
			apiFetch<TicketCommentListResponse>(
				ticketCommentsApiPath(propertyId as string, ticketId as string),
			),
		enabled: !!propertyId && !!ticketId,
		retry: (failureCount, error) => {
			if (error instanceof ApiError && error.status === 404) return false
			return failureCount < 3
		},
	})
}

export function useCreateTicketComment(propertyId: string, ticketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateTicketCommentBody) =>
			apiFetch<TicketCommentResponse>(
				ticketCommentsApiPath(propertyId, ticketId),
				{
					method: "POST",
					body: JSON.stringify(body),
				},
			),
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}

export function useDeleteTicketComment(propertyId: string, ticketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (commentId: string) =>
			apiFetch<{ deleted: true }>(
				ticketCommentApiPath(propertyId, ticketId, commentId),
				{ method: "DELETE" },
			),
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}

export function useUpdateTicketComment(propertyId: string, ticketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (input: { commentId: string; body: CreateTicketCommentBody }) =>
			apiFetch<TicketCommentResponse>(
				ticketCommentApiPath(propertyId, ticketId, input.commentId),
				{
					method: "PATCH",
					body: JSON.stringify(input.body),
				},
			),
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}

export function useUploadTicketAttachment(
	propertyId: string,
	ticketId: string,
) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: async (input: {
			file: File
			target?: TicketAttachmentTarget
		}) => {
			const body: CreateTicketAttachmentBody = {
				contentType: input.file.type,
				filename: input.file.name,
				declaredSize: input.file.size,
			}
			const created = await apiFetch<TicketAttachmentUploadResponse>(
				ticketAttachmentCreateApiPath(propertyId, ticketId, input.target),
				{
					method: "POST",
					body: JSON.stringify(body),
				},
			)
			await uploadToCloudinary(created.upload, input.file)
			return apiFetch<TicketAttachmentResponse>(
				ticketAttachmentConfirmApiPath(
					propertyId,
					ticketId,
					created.attachmentId,
				),
				{ method: "POST" },
			)
		},
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}

export function useDeleteTicketAttachment(
	propertyId: string,
	ticketId: string,
) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (fileId: string) =>
			apiFetch<{ deleted: true }>(
				ticketAttachmentDeleteApiPath(propertyId, ticketId, fileId),
				{ method: "DELETE" },
			),
		onSuccess: async () => {
			await invalidateTicketQueries(queryClient, propertyId, ticketId)
		},
	})
}
